/**
 * CameraCaptureDialog — USB webcam ID card capture (front + back) for OCR.
 *
 * Story 70-1D (E-70 OCR pipeline). Desktop-only: Chrome / Firefox on USB cam
 * per Q7 (`_bmad-output/ecard-reader-fit-gap-2026-04-11.md` L401-403).
 * iOS Safari fallback explicitly OUT OF SCOPE.
 *
 * Output: `{ front: Blob, back: Blob }` via `onCaptured` — channel-agnostic.
 * Consumer decides what to do next (e.g. pass to `@kgc/tesseract-provider`).
 *
 * Zero-retention by architecture (ADR-104 D-4 + GDPR DPIA Light §5):
 * - MediaStream tracks stopped on close + on unmount
 * - Blob URLs (thumbnails) revoked
 * - Canvas cleared + dimensions reset to 0
 * - Image refs nulled for V8 GC
 *
 * Design system: Precision Glass — teal accent #14B8A6, 2px angular borders.
 */

import { useTranslation } from '@kgc/i18n';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@kgc/ui';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useCameraCapture } from '@/hooks/use-camera-capture';

const TEAL = '#14B8A6';

export interface CameraCaptureResult {
  front: Blob;
  back: Blob;
}

export interface CameraCaptureDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCaptured: (images: CameraCaptureResult) => void;
  onClose?: () => void;
}

type Step = 'permission' | 'front' | 'back' | 'review';

export function CameraCaptureDialog({
  open,
  onOpenChange,
  onCaptured,
  onClose,
}: CameraCaptureDialogProps) {
  const { t } = useTranslation();
  const { permission, error, videoRef, canvasRef, focusModeSupported, start, stop, captureFrame } =
    useCameraCapture();

  const [step, setStep] = useState<Step>('permission');
  const [frontBlob, setFrontBlob] = useState<Blob | null>(null);
  const [backBlob, setBackBlob] = useState<Blob | null>(null);
  const [frontThumb, setFrontThumb] = useState<string | null>(null);
  const [backThumb, setBackThumb] = useState<string | null>(null);

  const frontThumbRef = useRef<string | null>(null);
  const backThumbRef = useRef<string | null>(null);

  // Keep refs in sync so the cleanup effect always sees the latest URL,
  // even if state updates haven't flushed yet.
  useEffect(() => {
    frontThumbRef.current = frontThumb;
  }, [frontThumb]);
  useEffect(() => {
    backThumbRef.current = backThumb;
  }, [backThumb]);

  // Full cleanup: stop stream, revoke blob URLs, wipe buffers.
  const dispose = useCallback((): void => {
    stop();
    if (frontThumbRef.current) {
      try {
        URL.revokeObjectURL(frontThumbRef.current);
      } catch {
        // ignore
      }
      frontThumbRef.current = null;
    }
    if (backThumbRef.current) {
      try {
        URL.revokeObjectURL(backThumbRef.current);
      } catch {
        // ignore
      }
      backThumbRef.current = null;
    }
    setFrontThumb(null);
    setBackThumb(null);
    setFrontBlob(null);
    setBackBlob(null);
  }, [stop]);

  // Reset + start camera when opened; dispose when closed.
  useEffect(() => {
    if (open) {
      setStep('permission');
    } else {
      dispose();
      setStep('permission');
    }
  }, [open, dispose]);

  // Defense in depth — unmount cleanup.
  useEffect(() => {
    return () => {
      dispose();
    };
  }, [dispose]);

  const handleRequestPermission = useCallback(async (): Promise<void> => {
    await start();
  }, [start]);

  // When permission is granted, advance to "front" capture.
  useEffect(() => {
    if (open && permission === 'granted' && step === 'permission') {
      setStep('front');
    }
  }, [open, permission, step]);

  const handleCapture = useCallback(async (): Promise<void> => {
    const blob = await captureFrame();
    if (!blob) return;
    const url = URL.createObjectURL(blob);
    if (step === 'front') {
      setFrontBlob(blob);
      setFrontThumb(url);
      setStep('back');
    } else if (step === 'back') {
      setBackBlob(blob);
      setBackThumb(url);
      setStep('review');
    }
  }, [captureFrame, step]);

  const handleRetakeFront = useCallback((): void => {
    if (frontThumb) {
      try {
        URL.revokeObjectURL(frontThumb);
      } catch {
        // ignore
      }
    }
    setFrontBlob(null);
    setFrontThumb(null);
    setStep('front');
  }, [frontThumb]);

  const handleRetakeBack = useCallback((): void => {
    if (backThumb) {
      try {
        URL.revokeObjectURL(backThumb);
      } catch {
        // ignore
      }
    }
    setBackBlob(null);
    setBackThumb(null);
    setStep('back');
  }, [backThumb]);

  const handleUseCaptures = useCallback((): void => {
    if (!frontBlob || !backBlob) return;
    // Deliver blobs BEFORE disposing thumbnails — consumer needs the Blob
    // references intact. Object URLs are the dialog's private state.
    onCaptured({ front: frontBlob, back: backBlob });
    // Keep blobs referenced by consumer; we just clean up our thumbs + stream.
    onOpenChange(false);
    onClose?.();
  }, [frontBlob, backBlob, onCaptured, onOpenChange, onClose]);

  const handleClose = useCallback((): void => {
    onOpenChange(false);
    onClose?.();
  }, [onOpenChange, onClose]);

  const currentStepIndex = step === 'front' ? 1 : step === 'back' ? 2 : step === 'review' ? 2 : 0;

  const errorMessage = (() => {
    if (!error) return null;
    if (error === 'permission-denied') return t('camera.permissionDenied');
    if (error === 'device-not-found') return t('camera.deviceNotFound');
    return t('camera.deviceGeneric');
  })();

  return (
    <Dialog
      open={open}
      onOpenChange={next => {
        if (!next) handleClose();
        else onOpenChange(next);
      }}
    >
      <DialogContent
        className="sm:max-w-2xl border-2 dark:bg-gray-900"
        style={{ borderColor: TEAL }}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span>{t('camera.title')}</span>
            {step !== 'permission' && (
              <span
                className="text-sm font-normal text-muted-foreground"
                data-testid="camera-step-counter"
              >
                {t('camera.stepCounter', { current: currentStepIndex, total: 2 })}
              </span>
            )}
          </DialogTitle>
          <DialogDescription>{t('camera.description')}</DialogDescription>
        </DialogHeader>

        {/* Permission gate */}
        {step === 'permission' && (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">{t('camera.permissionPrompt')}</p>
            {errorMessage && (
              <div
                role="alert"
                className="rounded-none border-2 border-destructive bg-destructive/10 p-3 text-sm text-destructive"
              >
                {errorMessage}
              </div>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={handleClose}>
                {t('camera.cancel')}
              </Button>
              <Button
                onClick={() => void handleRequestPermission()}
                disabled={permission === 'requesting'}
                style={{ backgroundColor: TEAL, borderColor: TEAL }}
              >
                {permission === 'requesting' ? t('camera.starting') : t('camera.permissionRequest')}
              </Button>
            </div>
          </div>
        )}

        {/* Capture steps (front + back) */}
        {(step === 'front' || step === 'back') && (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              {step === 'front' ? t('camera.instructionFront') : t('camera.instructionBack')}
            </p>
            <div
              className="relative overflow-hidden border-2"
              style={{ borderColor: TEAL, aspectRatio: '16 / 9' }}
            >
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                className="h-full w-full object-cover"
                data-testid="camera-video"
              />
            </div>
            <canvas ref={canvasRef} className="hidden" data-testid="camera-canvas" />
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>{focusModeSupported ? t('camera.focusManual') : t('camera.focusAuto')}</span>
              <span>{t('camera.zeroRetentionHint')}</span>
            </div>
            {frontThumb && (
              <div className="flex items-center gap-2">
                <img
                  src={frontThumb}
                  alt={t('camera.thumbnailFront')}
                  className="h-16 w-24 border-2 object-cover"
                  style={{ borderColor: TEAL }}
                  data-testid="camera-thumb-front"
                />
                <span className="text-xs text-muted-foreground">{t('camera.thumbnailFront')}</span>
              </div>
            )}
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={handleClose}>
                {t('camera.cancel')}
              </Button>
              <Button
                onClick={() => void handleCapture()}
                style={{ backgroundColor: TEAL, borderColor: TEAL }}
                data-testid={step === 'front' ? 'camera-capture-front' : 'camera-capture-back'}
              >
                {step === 'front' ? t('camera.captureFront') : t('camera.captureBack')}
              </Button>
            </DialogFooter>
          </div>
        )}

        {/* Review */}
        {step === 'review' && (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">{t('camera.zeroRetentionHint')}</p>
            <div className="grid grid-cols-2 gap-3">
              <figure className="space-y-1">
                {frontThumb && (
                  <img
                    src={frontThumb}
                    alt={t('camera.thumbnailFront')}
                    className="w-full border-2 object-cover"
                    style={{ borderColor: TEAL, aspectRatio: '16 / 9' }}
                    data-testid="camera-review-front"
                  />
                )}
                <figcaption className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>{t('camera.thumbnailFront')}</span>
                  <button
                    type="button"
                    onClick={handleRetakeFront}
                    className="underline hover:text-foreground"
                  >
                    {t('camera.retake')}
                  </button>
                </figcaption>
              </figure>
              <figure className="space-y-1">
                {backThumb && (
                  <img
                    src={backThumb}
                    alt={t('camera.thumbnailBack')}
                    className="w-full border-2 object-cover"
                    style={{ borderColor: TEAL, aspectRatio: '16 / 9' }}
                    data-testid="camera-review-back"
                  />
                )}
                <figcaption className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>{t('camera.thumbnailBack')}</span>
                  <button
                    type="button"
                    onClick={handleRetakeBack}
                    className="underline hover:text-foreground"
                  >
                    {t('camera.retake')}
                  </button>
                </figcaption>
              </figure>
            </div>
            <canvas ref={canvasRef} className="hidden" data-testid="camera-canvas" />
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={handleClose}>
                {t('camera.cancel')}
              </Button>
              <Button
                onClick={handleUseCaptures}
                disabled={!frontBlob || !backBlob}
                style={{ backgroundColor: TEAL, borderColor: TEAL }}
                data-testid="camera-use-captures"
              >
                {t('camera.useCaptures')}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
